import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { exchangeAuthorizationCode, useAuth } from '@edusense/shared-auth-core';
import { buildMfaRedirectTarget, resolveStoredRedirectTarget } from '../auth/adminMfaRouting';
import { ADMIN_HOME_PATH } from '../navigation/adminNavigation';

const AUTH_SERVER_URL = import.meta.env['VITE_AUTH_SERVER_URL'] || 'https://auth.ugot.uk';
const API_SERVER_URL = import.meta.env['VITE_API_URL'] || 'https://api.ugot.uk';
const CLIENT_ID = 'admin-client';
const REDIRECT_URI = `${window.location.origin}/auth/callback`;
const USE_PROXY = false;

const clearOAuthArtifacts = () => {
  localStorage.removeItem('oauth2_state');
  localStorage.removeItem('pkce_code_verifier');
  localStorage.removeItem('oauth2_session_id');
  localStorage.removeItem('oauth2_post_login_redirect');
  localStorage.removeItem('oauth2_post_login_lang');
};

const resolveTargetPath = (storedRedirect: string | null, storedState: string | null): string => {
  return resolveStoredRedirectTarget(storedRedirect, storedState) ?? ADMIN_HOME_PATH;
};

export default function AdminOAuth2CallbackPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setAuthenticatedState } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;

    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const incomingState = params.get('state');
    const oauthError = params.get('error');

    if (!code && !oauthError) {
      setIsLoading(false);
      return;
    }

    if (oauthError === 'mfa_required') {
      processed.current = true;
      navigate(
        buildMfaRedirectTarget({
          nextParam: params.get('next'),
          challengeIdParam: params.get('challengeId') || params.get('mfa_challenge_id'),
          storedRedirect: localStorage.getItem('oauth2_post_login_redirect'),
          storedState: localStorage.getItem('oauth2_state'),
          fallbackPath: ADMIN_HOME_PATH,
        }),
        { replace: true },
      );
      return;
    }

    processed.current = true;

    const run = async () => {
      const storedState = localStorage.getItem('oauth2_state');
      const codeVerifier = localStorage.getItem('pkce_code_verifier');
      const storedRedirect = localStorage.getItem('oauth2_post_login_redirect');
      const targetPath = resolveTargetPath(storedRedirect, storedState);

      try {
        if (oauthError) {
          throw new Error(oauthError === 'access_denied' ? '로그인이 취소되었습니다.' : '로그인 중 오류가 발생했습니다.');
        }

        if (!code) {
          throw new Error('인가 코드가 없습니다.');
        }

        if (!storedState || storedState !== incomingState) {
          throw new Error('보안 검증에 실패했습니다. 다시 로그인해주세요.');
        }

        if (!codeVerifier) {
          throw new Error('PKCE 검증 값을 찾을 수 없습니다.');
        }

        if (!setAuthenticatedState) {
          throw new Error('AuthProvider가 초기화되지 않았습니다.');
        }

        const { tokenData, user } = await exchangeAuthorizationCode(
          code,
          codeVerifier,
          REDIRECT_URI,
          CLIENT_ID,
          { USE_PROXY, AUTH_SERVER_URL, API_SERVER_URL },
        );

        await setAuthenticatedState(
          user,
          tokenData.access_token,
          tokenData.refresh_token,
          tokenData.expires_in,
          tokenData.token_type || 'Bearer',
          tokenData.id_token,
        );

        clearOAuthArtifacts();
        navigate(targetPath, { replace: true });
      } catch (callbackError) {
        console.error('[AdminOAuth2CallbackPage] OAuth callback failed:', callbackError);
        setError(callbackError instanceof Error ? callbackError.message : '로그인 처리 중 오류가 발생했습니다.');
        setTimeout(() => navigate('/landing', { replace: true }), 2000);
      } finally {
        setIsLoading(false);
      }
    };

    run().catch((unexpectedError) => {
      console.error('[AdminOAuth2CallbackPage] Unexpected callback error:', unexpectedError);
      setError('로그인 처리 중 오류가 발생했습니다.');
      setIsLoading(false);
    });
  }, [location.search, navigate, setAuthenticatedState]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md rounded-xl bg-white p-8 text-center shadow-sm border border-slate-200/60 space-y-4">
        <h1 className="text-xl font-semibold text-slate-800">
          {isLoading ? '로그인 처리 중...' : error ? '로그인 실패' : '로그인 완료'}
        </h1>
        <p className="text-sm text-slate-500">
          {isLoading
            ? '관리자 대시보드로 이동하고 있습니다.'
            : error || '잠시만 기다려주세요.'}
        </p>
      </div>
    </div>
  );
}
